import { z } from 'zod';

// Base backend configuration schema
const BaseBackendConfigSchema = z.object({
  type: z.enum(['aws', 'standalone']),
});

// AWS-specific configuration
const AWSConfigSchema = BaseBackendConfigSchema.extend({
  type: z.literal('aws'),
  auth: z.object({
  }),
  data: z.object({
    graphqlEndpoint: z.string().url(),
    region: z.string(),
  }),
  storage: z.object({
    bucket: z.string(),
    region: z.string(),
  }),
  events: z.object({
    snsTopicArn: z.string().optional(),
    sqsQueueUrl: z.string().optional(),
  }),
});

// Standalone-specific configuration
const StandaloneConfigSchema = BaseBackendConfigSchema.extend({
  type: z.literal('standalone'),
  auth: z.object({
  }),
  data: z.object({
    graphqlEndpoint: z.string().url(),
    postgresUrl: z.string(),
  }),
  storage: z.object({
    type: z.enum(['local', 'minio', 's3-compatible']),
    config: z.record(z.string(), z.any()), // Flexible config for different providers
  }),
  events: z.object({
    redisUrl: z.string().optional(),
    jobQueueConfig: z.record(z.string(), z.any()).default({}),
  }),
});

// Union type for all backend configurations
export const BackendConfigSchema = z.discriminatedUnion('type', [
  AWSConfigSchema,
  StandaloneConfigSchema,
]);

// TypeScript types derived from schemas
export type BackendConfig = z.infer<typeof BackendConfigSchema>;
export type AWSConfig = z.infer<typeof AWSConfigSchema>;
export type StandaloneConfig = z.infer<typeof StandaloneConfigSchema>;

// Configuration validation function
export function validateBackendConfig(config: unknown): BackendConfig {
  try {
    return BackendConfigSchema.parse(config);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errorMessages = error.issues.map((err: any) => 
        `${err.path.join('.')}: ${err.message}`
      ).join('\n');
      throw new Error(`Invalid backend configuration:\n${errorMessages}`);
    }
    throw error;
  }
}